'use client';

import { type DragEndEvent, type DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useState } from 'react';
import { validateDragOperation } from '../_utils/drag-utils';
import type { FileItem } from '../types';
import { useMoveFile } from './use-move-file';

export interface UseDragDropOptions {
  files: FileItem[];
  selectedItems: string[];
  onSelectionChange: (selectedItems: string[]) => void;
  onMoveSuccess?: (movedItemIds: string[]) => void;
}

export function useDragDrop({ files, selectedItems, onSelectionChange, onMoveSuccess }: UseDragDropOptions) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [movingItemIds, setMovingItemIds] = useState<string[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const { moveFiles, movingFiles } = useMoveFile({
    onSuccess: (successIds) => {
      // 清除选中状态
      onSelectionChange([]);

      // 调用成功回调，传递移动的文件信息
      onMoveSuccess?.(successIds);

      // 清除引用
      setMovingItemIds([]);
    },
    onError: () => {
      // 出错时清理移动中的状态
      setMovingItemIds([]);
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    const draggedId = event.active.id.toString();
    setActiveId(draggedId);

    // 如果拖拽的文件不在选中列表中，则将其添加到选中列表
    if (!selectedItems.includes(draggedId)) {
      onSelectionChange([draggedId]);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || movingFiles) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    // 使用工具函数验证拖拽操作
    const { isValid, itemsToMove } = validateDragOperation(activeId, overId, selectedItems, files);

    if (!isValid) return;

    // 保存当前移动的文件信息
    setMovingItemIds(itemsToMove.map((i) => i.id));

    // 调用接口移动文件
    moveFiles(itemsToMove, overId);
  };

  return {
    activeId,
    sensors,
    handleDragStart,
    handleDragEnd,
    movingFiles,
    movingItemIds,
  };
}
